/**
 * Opt-in enhancement for <product-info data-configurator-unselect>.
 *
 * Unlike <product-info-configurator> (assets/product-info-configurator.js),
 * this does NOT override the shared ProductInfo class - the base
 * <product-info> class keeps swapping to each variant's own attached image
 * exactly as it already does for every other product on this section. We
 * only ever shadow updateMedia() on this ONE element instance (see
 * guardUpdateMedia below), leaving every other product-info on the page
 * unaffected.
 *
 * The picker snippet (snippets/product-variant-options.liquid) is told to
 * never render a value pre-checked/selected for these products, on every
 * render - not just the first one - so that Shopify's own partial-match
 * guessing for options the shopper hasn't touched yet never kicks in
 * (otherwise e.g. picking a size alone can make an untouched color option
 * resolve to, and visually show, its first available value - and, unguarded,
 * swap the image to that guessed variant's media too). Confirmed live: Shopify
 * always defaults an unspecified option to its first available value in
 * `option_values`-driven partial requests, it never leaves it blank.
 *
 * That means every AJAX section refresh (product-info.js's updateOptionValues)
 * replaces <variant-selects> with markup that has nothing checked/selected,
 * including the option the shopper just picked. We restore exactly what they
 * picked onto the fresh markup ourselves, the same way
 * <product-info-configurator> does it, but via a plain change listener +
 * MutationObserver instead of subclassing.
 */
(function () {
  function uncheckAll(variantSelects) {
    variantSelects.querySelectorAll('input[type="radio"]').forEach((r) => {
      r.checked = false;
    });
    variantSelects.querySelectorAll('select').forEach((s) => {
      s.selectedIndex = -1;
    });
  }

  function captureSelectedValueIds(variantSelects) {
    const checkedRadios = variantSelects.querySelectorAll('input[type="radio"]:checked');
    const selectedOptions = variantSelects.querySelectorAll('select option[selected]');
    return Array.from([...checkedRadios, ...selectedOptions])
      .map((el) => el.dataset?.optionValueId)
      .filter(Boolean);
  }

  function restoreSelectedValueIds(variantSelects, ids) {
    if (!ids?.length) return;
    ids.forEach((id) => {
      const safeId = CSS.escape(String(id));
      const radio = variantSelects.querySelector(`input[type="radio"][data-option-value-id="${safeId}"]`);
      if (radio) {
        radio.checked = true;
        return;
      }
      const opt = variantSelects.querySelector(`option[data-option-value-id="${safeId}"]`);
      if (opt) {
        opt.setAttribute('selected', 'selected');
        const select = opt.closest('select');
        if (select) select.value = opt.value;
      }
    });
  }

  function setUnconfigured(root) {
    root.classList.add('product-info-configurator--unconfigured');

    const idInput = root.querySelector('input[name="id"].product-variant-id');
    if (idInput) {
      idInput.value = '';
      idInput.setAttribute('disabled', 'disabled');
    }

    const submit = root.querySelector('[id^="ProductSubmitButton-"]');
    if (submit) submit.setAttribute('disabled', 'disabled');

    root.querySelectorAll('variant-selects [data-selected-value]').forEach((el) => {
      el.innerHTML = '';
    });
  }

  function setConfigured(root) {
    root.classList.remove('product-info-configurator--unconfigured');
    root.querySelectorAll('input[name="id"].product-variant-id').forEach((input) =>
      input.removeAttribute('disabled')
    );
  }

  function allOptionsSelected(root, variantSelects) {
    const total = parseInt(root.dataset.optionCount || '0', 10);
    if (total <= 0) return false;

    const checkedRadios = variantSelects.querySelectorAll('input[type="radio"]:checked').length;
    const filledSelects = Array.from(variantSelects.querySelectorAll('select')).filter(
      (s) => s.selectedIndex >= 0 && s.value
    ).length;

    return checkedRadios + filledSelects >= total;
  }

  // sections/main-product.liquid forces the "cover-1" media to the front of
  // every render of this section, including the AJAX section re-render
  // product-info.js fires after an option change - there's no reliable
  // Liquid-side signal to tell that apart from the real page load (Shopify's
  // Section Rendering API doesn't expose the AJAX fetch's option_values
  // query param through request.query_string, confirmed by testing live).
  // updateMedia()'s gallery diffing (assets/product-info.js) reorders the
  // live gallery to match that destination order, so left alone it would
  // reorder around cover-1 instead of the resolved variant and desync
  // Swiper's slide index from the DOM. Before letting updateMedia touch the
  // destination markup, move the resolved variant's own slide to the front
  // of it ourselves so the diffing has the right order to work from.
  function reorderDestinationForVariant(html, variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    const list = html.querySelector('media-gallery ul');
    if (!list) return;
    const target = list.querySelector(`li[data-media-id="${CSS.escape(String(variantFeaturedMediaId))}"]`);
    if (target && list.firstElementChild !== target) {
      list.insertBefore(target, list.firstElementChild);
    }
  }

  // Shopify can resolve variant.featured_media by defaulting an option the
  // shopper hasn't touched yet to its first available value (the same
  // partial-match guessing product-variant-options.liquid's checked/selected
  // suppression works around for the picker itself). Guard this ONE
  // instance's updateMedia so the image only ever swaps once every option
  // actually has a value the shopper picked, without touching the shared
  // ProductInfo class other products on the page still rely on unguarded.
  function guardUpdateMedia(root) {
    if (root._configuratorMediaGuarded) return;
    root._configuratorMediaGuarded = true;
    if (typeof root.updateMedia !== 'function') return;

    const original = root.updateMedia.bind(root);
    root.updateMedia = (html, variantFeaturedMediaId) => {
      const total = parseInt(root.dataset.optionCount || '0', 10);
      if (total > 0 && (root._configuratorSelectedIds || []).length < total) return;
      reorderDestinationForVariant(html, variantFeaturedMediaId);
      return original(html, variantFeaturedMediaId);
    };
  }

  // The base class's own swap (assets/product-info.js's viewTransition) leaves
  // the old <variant-selects> in the DOM, just hidden, for 500ms before
  // removing it. Since it shares the same radio `name`/`form` as the fresh
  // one, both briefly form one native radio group across two DOM nodes -
  // which can visibly flash a swatch as selected/unselected before the
  // browser's grouping settles. Remove the stale node immediately instead of
  // waiting out that window.
  function removeStaleVariantSelects(root, current) {
    root.querySelectorAll('variant-selects').forEach((el) => {
      if (el !== current) el.remove();
    });
  }

  function watchForSwap(root) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType !== 1) continue;
          const variantSelects = node.tagName === 'VARIANT-SELECTS' ? node : node.querySelector?.('variant-selects');
          if (!variantSelects) continue;

          removeStaleVariantSelects(root, variantSelects);
          uncheckAll(variantSelects);
          restoreSelectedValueIds(variantSelects, root._configuratorSelectedIds || []);

          if (allOptionsSelected(root, variantSelects)) {
            setConfigured(root);
          } else {
            setUnconfigured(root);
          }
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function initRoot(root) {
    if (root._configuratorUnselectInit) return;
    root._configuratorUnselectInit = true;
    root._configuratorSelectedIds = [];

    const initialVariantSelects = root.querySelector('variant-selects');
    if (initialVariantSelects) uncheckAll(initialVariantSelects);
    setUnconfigured(root);
    guardUpdateMedia(root);

    root.addEventListener('change', (event) => {
      const variantSelects = event.target.closest('variant-selects');
      if (!variantSelects) return;
      root._configuratorSelectedIds = captureSelectedValueIds(variantSelects);
    });

    watchForSwap(root);
  }

  function init() {
    document.querySelectorAll('[data-configurator-unselect]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
